import React, { forwardRef, useState } from 'react';
import {
  Button,
  Tooltip,
  FormGroup,
  createTheme,
  ThemeProvider,
  SvgIcon,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Form, Col, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import { object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import UserIcon from '../../../../icons/user.svg?react';
import {
  getBasicInfo,
  setBasicInfo,
} from '../../../../sessionStorage';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import TestimonialPanel from '../../../Common/Testimonials/TestimonialPanel';
import './AboutMe.scss';

const FinalExpenseAboutMeForm = forwardRef((props, ref) => {
  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 16,
          },
          root: {
            height: 56,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 16,
            color: "rgba(0,0,0,0.6)",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: {
            color: "rgba(0,0,0,0.3) !important",
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          fontSize: 16,
          icon: {
            color: "rgba(0,0,0,1)",
          },
          iconFilled: {
            right: 16,
          },
        },
      },
    },
  });

  const navigate = useNavigate();
  const basicInfo = getBasicInfo();
  const [formState, setFormState] = useState(FinalExpenseAboutMeForm.DefaultFormState(false, basicInfo));

  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  const formMethods = useForm({
    resolver: yupResolver(FinalExpenseAboutMeForm.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState
  });

  const { handleSubmit, trigger } = formMethods;

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
    if (field === 'gender') {
      trigger(field);
    }
  };

  const onSubmit = () => {
    // Save formState to basicinfo
    const basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = formState.gender;
    basicInfoFromSessionStorage.birthdate = formState.birthdate;
    basicInfoFromSessionStorage.state = formState.state;
    basicInfoFromSessionStorage.tobacco = formState.tobacco;
    basicInfoFromSessionStorage.usResident = formState.usResident;

    setBasicInfo(basicInfoFromSessionStorage);

    if (props.openEditPopUp) {
      navigate('/final-expense/quote-results');
      props.setOpenEditPopUp(false, null);
    } else {
      props.onNext();
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={`basic-info-about-me-final-expense`}>
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} ref={ref}>
            <Row>
              <Col lg={12} className='w-100'>
                {props.showHeader && (
                  <React.Fragment>
                    <Row className="justify-content-center">
                      <SvgIcon color='primary' component={UserIcon} className="icons" fontSize="large" />
                    </Row>
                    <Row className="justify-content-center">
                      <h3 className="py-3">Profile</h3>
                      <Tooltip
                        title="This information should represent..."
                        enterTouchDelay={0}
                        open={tooltip}
                        onClose={handleTooltipAboutClose}
                        slotProps={{
                          tooltip: {
                            className: 'light-tooltip',
                            sx: { fontSize: '14px' }
                          },
                        }}
                      >
                        <button
                          type="button"
                          id="about"
                          className="quote-tooltip"
                          onMouseEnter={handleTooltipAboutOpen}
                          onClick={handleTooltipAboutOpen}
                          aria-describedby="about-tooltip"
                        >
                          <InfoOutlinedIcon className="help-tooltip" />
                        </button>
                      </Tooltip>
                    </Row>
                  </React.Fragment>
                )}

                <Row>
                  <Col>
                    <FormGroup>
                      <StyledRadioInput
                        name="gender"
                        id="gender"
                        label=""
                        value={formState.gender}
                        options={[
                          { value: "F", label: "Female", icon: 'female' },
                          { value: "M", label: "Male", icon: 'male' },
                        ]}
                        onChange={e => handleValueChange('gender', e)}
                        isBlankIcon={true}
                        isBlankCheckMark={false}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group'>
                      <TextInput
                        name="birthdate"
                        id="birthdate"
                        label="Date of Birth"
                        value={formState.birthdate}
                        onChange={(e) => handleValueChange('birthdate', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskDate,
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group'>
                      <SelectInput
                        name="state"
                        id="state"
                        value={formState.state}
                        onChange={(e) => handleValueChange('state', e.target.value)}
                        label="State"
                        selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group mb-3'>
                      <SelectInput
                        name="tobacco"
                        id="tobacco"
                        value={formState.tobacco}
                        onChange={(e) => handleValueChange('tobacco', e.target.value)}
                        label="Tobacco/Nicotine Use"
                        selectOptions={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        aria-invalid="true"
                        aria-describedby="tobacco"
                      />
                    </div>
                  </Col>
                </Row>

              </Col>
              {props.showQuoteButton &&
                <Col xs={12}>
                  <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                    <ShieldCheckmarkIcon style={{ width: "20px" }} className='me-1' />UPDATE MY QUOTE</Button>
                </Col>
              }
            </Row>
            {props.quickQuoteDisclaimer && props.submitButtonVariant === 'landing' &&
              <Row>
                <Col className="quick-quote-disclaimer-container"
                  dangerouslySetInnerHTML={{ __html: props.quickQuoteDisclaimer }} />
              </Row>
            }

            <Row className="mt-0">
              {!props.submitButtonVariant && !props.hideNextButton &&
                <Col className="text-end pb-3">
                  <Button variant="contained" size="medium" color="primary" type="submit">
                    NEXT
                  </Button>
                </Col>
              }
              {props.submitButtonVariant && props.submitButtonVariant === 'landing' &&
                <Col className="d-flex mt-3">
                  <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                    <ShieldCheckmarkIcon className="button-icon" />CONTINUE</Button>
                </Col>
              }
            </Row>
            {!props.modal &&
              <TestimonialPanel />
            }
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  )
});

FinalExpenseAboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), {
        years: ageSpecs.min,
        days: ageSpecs.minAgeDays || 0,
      }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    tobacco: string().required('Tobacco usage is required.'),
  })
};

FinalExpenseAboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1950' : (basicInfo && basicInfo.birthdate) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    tobacco: prefill ? 'No' : (basicInfo && basicInfo.tobacco) || '',
    usResident: true,
  };
};

export default FinalExpenseAboutMeForm;
